import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Container,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import {
  Work,
  Business,
  CalendarToday,
  LocationOn,
  TrendingUp,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

const WorkHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showAllJobs, setShowAllJobs] = useState(false);

  const workExperience = [
    {
      company: 'Oobeo',
      position: 'Director of Product Development',
      duration: 'Feb 2018 - Present',
      location: 'Toledo OH (Remote)',
      description: 'SaaS parking and mobility platform and mobile payments solution',
      technologies: ['SaaS', 'Product Management', 'System Integration', 'Mobile Payments'],
      achievements: [
        'Owned product lifecycle, roadmap planning, and release cycles',
        'Achieved 99.999% uptime for 5 consecutive years',
        'Integrated 10+ discrete PMS, enforcement, POS and automation systems'
      ]
    },
    {
      company: 'GiveSmart',
      position: 'Senior Product Manager',
      duration: 'Jan 2017 - May 2018',
      location: 'Atlanta GA (Remote)',
      description: 'Events and donation SaaS offering (Acquired by Community Brands 2018)',
      technologies: ['Agile', 'Product Management', 'SaaS', 'Team Leadership'],
      achievements: [
        'Oversaw large agile product team for events and donation SaaS offering',
        'Responsible for roadmap planning, backlog grooming, sprint execution, UX and user feedback loops',
        'Managed onsite and remote teams on 3 continents in 4 time zones'
      ]
    },
    {
      company: 'Sohalo, Inc.',
      position: 'Design Director',
      duration: 'Sep 2011 - Oct 2016',
      location: 'San Francisco CA',
      description: 'Customer acquisition and engagement platform (Acquired by Student Universe 2016)',
      technologies: ['Design Leadership', 'Customer Engagement', 'Social Media', 'Analytics'],
      achievements: [
        'Managed cross-functional team helping clients to acquire, engage and convert customers by motivating, measuring, and rewarding customers\' social actions and brand loyalty',
        'Empowered marketers to create 10X higher engagement, 2X more revenue per customer, and 8X ROI with personalized social promotions and offers'
      ]
    },
    {
      company: 'Circle of Moms',
      position: 'Senior UI Developer',
      duration: 'Sep 2009 - Aug 2011',
      location: 'San Francisco CA',
      description: 'Largest social media platform for mothers (Acquired by PopSugar 2011)',
      technologies: ['Frontend Development', 'UI/UX Design', 'A/B Testing', 'Social Media'],
      achievements: [
        'Design manager and primary front end developer for largest social media platform for mothers',
        'Aggressive iteration of features using advanced A/B testing',
        'Advertising design and campaign planning'
      ]
    },
    {
      company: 'Vivaty',
      position: 'Front End Developer',
      duration: 'Jan 2008 - Sep 2009',
      location: 'San Francisco CA',
      description: 'Immersive 3D video chat application',
      technologies: ['Frontend Development', '3D Graphics', 'Video Production', 'Social Media Integration'],
      achievements: [
        'Principal designer and front end developer for immersive 3D video chat application',
        'Promotional graphics and pitch decks',
        'Video production, social media integration and content development'
      ]
    },
    {
      company: 'Indianapolis Star - Gannett',
      position: 'Digital Designer',
      duration: 'Jun 2006 - Dec 2007',
      location: 'Indianapolis IN',
      description: 'Innovation & Development Initiative for next-gen publishing and social applications',
      technologies: ['Digital Design', 'Publishing', 'Social Applications', 'Innovation'],
      achievements: [
        'First hire for Innovation & Development Initiative building next-gen publishing and social applications for Gannett Corporate'
      ]
    }
  ];

  const displayedJobs = showAllJobs ? workExperience : workExperience.slice(0, 2);

  return (
    <Box
      id="work"
      sx={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        py: 8,
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Professional Experience
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Technical product manager with 20 years delivering SaaS, social and B2B products
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {displayedJobs.map((job, index) => (
            <Grid item xs={12} key={index}>
              <Card
                sx={{
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Business sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" fontWeight={600}>
                            {job.company}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <Work sx={{ mr: 0.5, fontSize: '1rem', verticalAlign: 'middle' }} />
                          {job.position}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <CalendarToday sx={{ mr: 0.5, fontSize: '1rem', verticalAlign: 'middle' }} />
                          {job.duration}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <LocationOn sx={{ mr: 0.5, fontSize: '1rem', verticalAlign: 'middle' }} />
                          {job.location}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={9}>
                      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                        {job.description}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Key Technologies:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {job.technologies.map((tech, techIndex) => (
                            <Chip
                              key={techIndex}
                              label={tech}
                              size="small"
                              sx={{
                                background: `linear-gradient(45deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Key Achievements:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {job.achievements.map((achievement, achievementIndex) => (
                            <Typography
                              key={achievementIndex}
                              component="li"
                              variant="body2"
                              sx={{ mb: 0.5, lineHeight: 1.5 }}
                            >
                              {achievement}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {workExperience.length > 2 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => setShowAllJobs(!showAllJobs)}
              startIcon={showAllJobs ? <ExpandLess /> : <ExpandMore />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              {showAllJobs ? 'Show Less' : 'See More'}
            </Button>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
            Continuous Growth & Learning
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Always exploring new technologies and methodologies to deliver better solutions
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default WorkHistory; 